import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Flame, Sparkles, Leaf, Drumstick, Sun, Moon, Dumbbell, Activity, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useSEO, SITE_URL } from '@/lib/seo';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { Breadcrumbs } from '@/components/Breadcrumbs';

const HEALTH_DISCLAIMER =
  'Health & Nutrition Disclaimer: This meal plan is provided for general informational and educational purposes only, and is not medical, nutritional or dietary advice. Calorie and nutrient needs vary considerably from person to person. If you have a medical condition, take medication, are pregnant or breastfeeding, or have specific dietary requirements, consult a qualified healthcare professional or registered dietitian before making significant dietary changes or starting a new exercise routine.';

const CALORIE_NOTE =
  'Calorie figures shown against each meal are approximate estimates based on the listed ingredients and assumed serving sizes. They have not been laboratory tested, and individual energy needs vary considerably, so this page does not set a daily calorie target.';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MealBlock = ({ title, meal }) => {
  if (!meal) return null;
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#1C1917]/5">
      <p className="text-xs uppercase tracking-[0.25em] text-[#F97316] font-['Outfit'] font-semibold mb-2">{title}</p>
      <h4 className="font-['Playfair_Display'] text-xl font-bold text-[#1C1917] mb-3">{meal.name}</h4>
      {meal.calories && (
        <p className="text-xs text-[#1C1917]/60 font-['Outfit'] mb-4">≈ {meal.calories} kcal (estimate)</p>
      )}
      {meal.ingredients?.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-[#1C1917] font-['Outfit'] mb-1">Ingredients</p>
          <ul className="text-sm text-[#1C1917]/70 font-['Outfit'] space-y-1">
            {meal.ingredients.map((i, idx) => (
              <li key={`${i}-${idx}`} className="flex gap-2"><span className="text-[#F97316]">•</span>{i}</li>
            ))}
          </ul>
        </div>
      )}
      {meal.steps?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-[#1C1917] font-['Outfit'] mb-1">Method (no added oil)</p>
          <ol className="text-sm text-[#1C1917]/70 font-['Outfit'] space-y-1 list-decimal pl-4">
            {meal.steps.map((s, idx) => <li key={`${s.slice(0, 20)}-${idx}`}>{s}</li>)}
          </ol>
        </div>
      )}
    </div>
  );
};

const intensityClass = (level) => {
  if (level === 'Hard') return 'bg-[#F97316]/20 text-[#F97316]';
  if (level === 'Moderate') return 'bg-[#2D4A22]/15 text-[#2D4A22]';
  return 'bg-[#1C1917]/10 text-[#1C1917]/70';
};

const sessionIcon = (type) => {
  if (/bodyweight|hiit|strength/i.test(type)) return <Dumbbell className="w-4 h-4" />;
  if (/walk|jog|hike|swim|cycle/i.test(type)) return <Activity className="w-4 h-4" />;
  return <Sparkles className="w-4 h-4" />;
};

const ExerciseBlock = ({ exercise }) => {
  if (!exercise) return null;
  return (
    <div className="bg-white/80 rounded-2xl p-5 border border-[#1C1917]/5 shadow-sm">
      <p className="text-xs uppercase tracking-[0.25em] text-[#2D4A22] font-['Outfit'] font-semibold mb-3 flex items-center gap-1.5">
        <Dumbbell className="w-3 h-3" /> Today's Movement
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        {['morning', 'evening'].map((slot) => {
          const s = exercise[slot];
          if (!s) return null;
          const Icon = slot === 'morning' ? Sun : Moon;
          return (
            <div key={slot} className="bg-[#FDFBF7] rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5 text-[#F97316]" />
                  <span className="text-[10px] uppercase tracking-widest text-[#1C1917]/60 font-['Outfit'] font-semibold">
                    {slot}
                  </span>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-['Outfit'] font-semibold ${intensityClass(s.intensity)}`}>
                  {s.intensity}
                </span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                {sessionIcon(s.type)}
                <h5 className="font-['Playfair_Display'] font-bold text-[#1C1917]">{s.type}</h5>
              </div>
              <p className="text-xs text-[#F97316] font-['Outfit'] font-semibold mb-1.5">{s.duration}</p>
              <p className="text-sm text-[#1C1917]/70 font-['Outfit'] leading-relaxed">{s.details}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DayCard = ({ entry }) => (
  <div
    data-testid={`challenge-day-${entry.day}`}
    className="bg-gradient-to-br from-[#FEF3E2] to-[#FDFBF7] rounded-3xl p-8 shadow-sm border border-[#1C1917]/5"
  >
    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#F97316] text-white flex items-center justify-center font-['Playfair_Display'] text-xl font-bold">
          {entry.day}
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-[#1C1917]/50 font-['Outfit']">Day {entry.day}</p>
          <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#1C1917]">{entry.theme}</h3>
        </div>
      </div>
      <span className="px-3 py-1 bg-[#2D4A22] text-white rounded-full text-xs font-['Outfit'] font-medium">Day {entry.day}</span>
    </div>

    <div className="grid md:grid-cols-3 gap-4 mb-4">
      <MealBlock title="Breakfast" meal={entry.breakfast} />
      <MealBlock title="Lunch" meal={entry.lunch} />
      <MealBlock title="Dinner" meal={entry.dinner} />
    </div>

    <div className="grid md:grid-cols-2 gap-4 mb-4">
      {entry.snack && (
        <div className="bg-white/80 rounded-xl px-5 py-4 text-sm font-['Outfit']">
          <span className="font-semibold text-[#F97316]">Snack: </span>
          <span className="text-[#1C1917]/80">{entry.snack}</span>
        </div>
      )}
      {entry.tip && (
        <div className="bg-[#2D4A22]/10 rounded-xl px-5 py-4 text-sm font-['Outfit']">
          <span className="font-semibold text-[#2D4A22]">Kitchen tip: </span>
          <span className="text-[#1C1917]/80">{entry.tip}</span>
        </div>
      )}
    </div>

    <ExerciseBlock exercise={entry.exercise} />
  </div>
);

const DIET_KEY = 'masala-spin-challenge-diet';

export default function Challenge() {
  const [plan, setPlan] = useState(null);
  const [diet, setDiet] = useState(() => {
    try {
      return localStorage.getItem(DIET_KEY) || 'non-veg';
    } catch {
      return 'non-veg';
    }
  });

  const loadPlan = useCallback(async (currentDiet) => {
    try {
      const { data } = await axios.get(`${API}/challenge/plan`, { params: { diet: currentDiet } });
      setPlan(data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load challenge plan:', err);
      }
      toast.error('Failed to load challenge plan');
    }
  }, []);

  useEffect(() => { loadPlan(diet); }, [loadPlan, diet]);

  const changeDiet = (d) => {
    setDiet(d);
    try {
      localStorage.setItem(DIET_KEY, d);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Challenge: localStorage unavailable, diet preference not persisted', err);
      }
    }
    toast.success(d === 'veg' ? 'Switched to vegetarian plan 🌱' : 'Switched to non-veg plan');
  };

  useSEO({
    title: '21-Day Indian Meal Plan — Millet-Focused, Low-Oil Recipes | Masala Spin',
    description:
      'A free 21-day Indian meal plan: millet-focused, low-oil recipes for breakfast, lunch, dinner and snacks, with vegetarian and non-vegetarian tracks and an optional daily movement routine. General information only.',
    canonical: `${SITE_URL}/challenge`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '21-Day Indian Meal Plan: Millet-Focused, Low-Oil Recipes',
      url: `${SITE_URL}/challenge`,
      isPartOf: { '@type': 'WebSite', name: 'Masala Spin', url: SITE_URL },
      publisher: { '@type': 'Organization', name: 'Masala Spin', url: SITE_URL },
    },
  });


  return (
    <div className="min-h-screen" style={{ background: '#FDFBF7' }}>
      {/* Header */}
      <SiteHeader />

      {/* Hero */}
      <section className="py-16 px-6 bg-gradient-to-br from-[#2D4A22] via-[#1C1917] to-[#F97316] text-white overflow-hidden relative">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20viewBox%3D%270%200%20400%20400%27%20xmlns%3D%27http%3A//www.w3.org/2000/svg%27%3E%3Cfilter%20id%3D%27n%27%3E%3CfeTurbulence%20baseFrequency%3D%270.9%27/%3E%3C/filter%3E%3Crect%20width%3D%27100%25%27%20height%3D%27100%25%27%20filter%3D%27url(%23n)%27%20opacity%3D%270.15%27/%3E%3C/svg%3E')] mix-blend-overlay pointer-events-none" />
        <div className="max-w-5xl mx-auto relative">
          <div className="flex items-center gap-2 mb-4">
            <Flame className="w-5 h-5 text-[#FBBF24]" />
            <span className="text-xs uppercase tracking-[0.3em] font-['Outfit'] font-semibold text-[#FBBF24]">21-Day Meal Plan</span>
          </div>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            21-Day Indian Meal Plan:<br />Millet-Focused, Low-Oil Recipes
          </h1>
          <p className="text-base sm:text-lg text-white/85 font-['Outfit'] max-w-3xl leading-relaxed mb-8">
            A structured collection of 21 days of Indian meals — breakfast, lunch, dinner and a
            snack for each day, with ingredients and a method for every dish, plus an optional
            morning and evening movement routine. The recipes lean on millets and legumes rather
            than refined flour, use dates instead of added sugar, and are written to be cooked with
            little or no added oil. Some people find a ready-made plan useful when they want to
            organise their cooking for a few weeks; individual nutrition and energy needs vary from
            person to person. Follow it as written, adapt it, or just use it as a recipe list.
          </p>
          <div className="flex flex-wrap gap-3">
            {['No white rice, chapati or maida', 'No dairy', 'Dates instead of added sugar', 'No added cooking oil', 'Millet-focused', 'Optional AM + PM movement'].map((b) => (
              <span key={b} className="px-4 py-2 bg-white/15 backdrop-blur rounded-full text-sm font-['Outfit'] border border-white/20">
                <Sparkles className="w-3 h-3 inline mr-1" />{b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Breadcrumbs + health disclaimer */}
      <section className="pt-8 px-6 max-w-5xl mx-auto">
        <Breadcrumbs items={[{ name: '21-Day Meal Plan' }]} />
        <aside
          data-testid="challenge-health-disclaimer"
          aria-label="Health and nutrition disclaimer"
          className="p-5 rounded-2xl border border-[#F97316]/30 bg-[#F97316]/5"
        >
          <p className="flex items-start gap-2.5 font-['Outfit'] text-sm text-[#1C1917]/80 leading-relaxed">
            <ShieldAlert className="w-5 h-5 text-[#F97316] mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{HEALTH_DISCLAIMER}</span>
          </p>
          <p className="font-['Outfit'] text-xs text-[#1C1917]/60 mt-3 pl-8">
            {CALORIE_NOTE} For how the plan is put together, read{' '}
            <Link
              to="/blog/21-day-indian-weight-loss-meal-plan-oil-free-millet"
              className="text-[#F97316] hover:underline"
            >
              our guide to this 21-day meal plan
            </Link>.
          </p>
        </aside>
      </section>

      {/* Diet toggle */}
      <section className="py-8 px-6 max-w-5xl mx-auto">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#1C1917]/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#1C1917]/55 font-['Outfit'] font-semibold mb-1">
              Choose your plan
            </p>
            <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1C1917]">
              Switch between vegetarian &amp; non-vegetarian
            </h3>
            <p className="text-sm text-[#1C1917]/60 font-['Outfit'] mt-1">
              Each non-vegetarian meal (chicken, fish, egg, prawn) includes a suggested vegetarian
              alternative using ingredients such as paneer, tofu, mushrooms or jackfruit, matched to
              the same regional flavours. The substitute dish will not have the same nutrition as the
              original.
            </p>
          </div>
          <div className="flex bg-[#FDFBF7] rounded-full p-1 border border-[#1C1917]/10 flex-shrink-0">
            <button
              onClick={() => changeDiet('non-veg')}
              data-testid="challenge-diet-nonveg"
              className={`px-5 py-2 rounded-full font-['Outfit'] font-semibold text-sm transition-all flex items-center gap-2 ${
                diet === 'non-veg' ? 'bg-[#F97316] text-white shadow-md' : 'text-[#1C1917]/60'
              }`}
            >
              <Drumstick className="w-4 h-4" /> Non-veg
            </button>
            <button
              onClick={() => changeDiet('veg')}
              data-testid="challenge-diet-veg"
              className={`px-5 py-2 rounded-full font-['Outfit'] font-semibold text-sm transition-all flex items-center gap-2 ${
                diet === 'veg' ? 'bg-[#2D4A22] text-white shadow-md' : 'text-[#1C1917]/60'
              }`}
            >
              <Leaf className="w-4 h-4" /> Vegetarian
            </button>
          </div>
        </div>
      </section>

      {/* Plan */}
      <section className="pb-12 px-6 max-w-5xl mx-auto space-y-6">
        {!plan && <p className="text-center py-12 text-[#1C1917]/60 font-['Outfit']">Loading your plan…</p>}
        {plan?.days.map((d) => (
          <DayCard key={d.day} entry={d} />
        ))}
      </section>

      <SiteFooter />
    </div>
  );
}
